import React, { useContext, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { captureException } from '@sentry/nextjs';
import styled from 'styled-components';
import { spacer12 } from '@spotify-internal/encore-web';

import { MarketContext } from 'providers/market';
import { DocumentLink } from 'lib/sanity/types';
import analytics from 'utils/client/analytics/analytics';
import { UbiHomePage } from 'utils/ubi/pages/useUbiHome';

import { getMinRequiredChars } from './search-utils';
import SearchInput from './SearchInput';
import SearchResults from './SearchResults';

const Container = styled.div`
  padding: ${spacer12} 0;
`;

export interface SearchProps {
  ubi: UbiHomePage;
}

const Search = ({ ubi }: SearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocumentLink[]>([]);
  const [debouncedQuery] = useDebounce(searchQuery, 500);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const { marketCode, locale } = useContext(MarketContext);

  const minRequiredChars = getMinRequiredChars(locale);
  const enteredMinSearchChars = debouncedQuery.length >= minRequiredChars;

  useEffect(() => {
    const fetchData = async (query: string) => {
      setIsSearching(true);
      ubi.logSearch(query);

      try {
        const response = await fetch(`/api/web/${marketCode}/search/?q=${query}`);
        if (!response.ok) {
          throw new Error(`Search API Error with status: ${response.status.toString()}`);
        }
        analytics.trackPageView(`/search/?q=${query}`);
        const results = await response.json();
        setSearchResults(results);
        setIsSearching(false);
        setShowResults(true);
      } catch (error) {
        setSearchResults([]);
        setIsSearching(false);
        setShowResults(true);
        captureException(error);
      }
    };
    if (enteredMinSearchChars) {
      fetchData(debouncedQuery);
    } else if (!enteredMinSearchChars) {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [debouncedQuery, enteredMinSearchChars, marketCode, ubi]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  return (
    <Container>
      <SearchInput
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSearching={isSearching}
        clearSearch={clearSearch}
      />
      {showResults && <SearchResults searchResults={searchResults} ubi={ubi} />}
    </Container>
  );
};

export default Search;
